import type { Rect } from "@gooner/shared";

/**
 * The rules a region's rectangle obeys, wherever it is being dragged.
 *
 * These lived in `editorStore.ts` alongside a Zustand store built around a Cave and its
 * Monitors. Caves are gone, and the store went with them — but the geometry was never
 * cave-shaped: it is what the canvas enforces while you draw, and both the canvas and the
 * editor read it. So it lives on its own, under a name that says what it is.
 */

/**
 * The editing grid: every edge lands on a twelfth.
 *
 * Twelve because it is what the existing layouts already are. Halves, thirds and quarters
 * are the only divisions the presets use, and 12 is the smallest number exactly divisible
 * by all three — 6, 4 and 3 cells. A grid of 10 would put every thirds layout permanently
 * off its own gridlines, and one of 16 would do the same to thirds while adding nothing
 * halves and quarters need. So this grid does not reshape a single existing layout; it
 * only makes the rule they already followed explicit and enforced.
 *
 * Freehand rectangles are what the grid is FOR. Dragged by eye, they landed on values like
 * 0.37 × 0.61 — a wall of tiles that never quite lined up, with seams a pixel wide that
 * looked like a rendering bug rather than a layout choice.
 */
export const GRID = 12;

/** One cell, as a fraction of the surface. */
export const CELL = 1 / GRID;

/** The smallest footprint a region may be dragged or resized to: one cell. */
export const MIN_REGION = CELL;

/** Clamp a rect into [0,1] while keeping a sensible minimum footprint. */
export function clampRect(rect: Rect): Rect {
  let w = Math.min(1, Math.max(MIN_REGION, rect.w));
  let h = Math.min(1, Math.max(MIN_REGION, rect.h));
  const x = Math.min(1 - w, Math.max(0, rect.x));
  const y = Math.min(1 - h, Math.max(0, rect.y));
  // Re-clamp size in case x/y pushed it past the edge.
  w = Math.min(w, 1 - x);
  h = Math.min(h, 1 - y);
  return { x, y, w, h };
}

/**
 * Clamp a rect and put every edge on a gridline.
 *
 * Snaps the EDGES rather than the origin and the size separately. Snapping an origin and
 * carrying the old size forward leaves the far edge wherever it was — so a region dragged
 * from an off-grid layout would line up on its left and still be a pixel out on its
 * right, which is the bug this is meant to end. Moving both edges also regularises the
 * legacy freehand rectangles the moment they are touched, which is what makes an old view
 * fit the grid rather than fight it.
 *
 * Containment and the one-cell minimum are `clampRect`'s, not this function's. A rect
 * dragged past the right wall is SLID back there rather than shrunk — a drag toward an
 * edge should stop against it, not get thinner — and that happens before any snapping,
 * because clampRect moves the origin rather than trimming the size.
 *
 * Pure, and exported, so the rule can be checked at every size without a pointer: the
 * failures it guards against (a collapsed region, a rect that leaves the surface, a size
 * that changes when you only meant to move) are invisible in a screenshot of one drag.
 */
export function snapRect(rect: Rect): Rect {
  // Containment and the one-cell minimum come from here, so the arithmetic below never
  // has to reproduce them: a clamped rect is already inside [0,1] and at least a cell in
  // each direction.
  const c = clampRect(rect);

  // Counted in whole cells rather than fractions. Snapping to `Math.round(v * GRID) / GRID`
  // and then subtracting reintroduces the float error the grid exists to remove — 8/12
  // minus 2/12 is 0.5000000000000001, which is expressible, not on any gridline, and
  // fails an equality check against the very grid that produced it.
  const cells = (v: number) => Math.round(v * GRID);
  const x = cells(c.x);
  const y = cells(c.y);

  // Edges, not origin-plus-size. Carrying an unsnapped width through a move would leave
  // the far edge off the grid forever: the region would line up on its left and still be
  // a fraction out on its right, which is the bug this is meant to end.
  //
  // `Math.max` keeps a region from collapsing if both its edges round to the same line.
  // Unreachable while clampRect guarantees a cell of width — kept so this function is
  // correct on its own terms rather than on another function's promise.
  const x2 = Math.max(x + 1, cells(c.x + c.w));
  const y2 = Math.max(y + 1, cells(c.y + c.h));

  return { x: x / GRID, y: y / GRID, w: (x2 - x) / GRID, h: (y2 - y) / GRID };
}

/** Is every edge of this rect already on a gridline? */
export function isOnGrid(rect: Rect): boolean {
  const onLine = (v: number) =>
    Math.abs(v * GRID - Math.round(v * GRID)) < 1e-6;
  return onLine(rect.x) && onLine(rect.y) && onLine(rect.w) && onLine(rect.h);
}
